import { Column, TablePaginatedOptions, TablePaginated } from '@acceso/design-system';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { RenderActionButtons } from '@/components/RenderActionButtons';
import { TableRow } from '@/components/TableRow';
import { TableUpdatedBy } from '@/components/TableUpdatedBy';
import { usePortainerGroups } from '@/features/portainer/api/get-portainer-groups';
import { useUserOptions } from '@/features/users/hooks/useUserOptions';
import { createTableFilterOptions } from '@/utils/table-filters';

import { useProductionCenterTypeOptions } from '../../hooks/useProductionCenterTypeOptions';
import { useProductionCenterFilters } from '../../stores/production-center-filters';
import { ProductionCenterType } from '../../types';

interface ParentProductionCenterTableProps {
  items: ProductionCenterType[];
  totalItems: number;
  activeItem: ProductionCenterType | null;
  onUpdate?: (productionCenter: ProductionCenterType) => void;
  onDuplicate?: (productionCenter: ProductionCenterType) => void;
  onDelete?: (productionCenter: ProductionCenterType) => void;
  onSelect?: (productionCenter: ProductionCenterType) => void;
  setSelectedUser: Dispatch<SetStateAction<ProductionCenterType | null>>;
  isFetching?: boolean;
}

export function ProductionCenterTable({
  items,
  totalItems,
  activeItem,
  onUpdate,
  onDelete,
  onSelect,
  isFetching,
}: ParentProductionCenterTableProps) {
  const { t } = useTranslation();
  const productionCenterTypeOptions = useProductionCenterTypeOptions();
  const userOptions = useUserOptions();
  const { setFilters, paginationValues, setPage, setRows, setSortBy, setSearchQuery } =
    useProductionCenterFilters();
  const { data: portainerGroups = [] } = usePortainerGroups();

  const options: Partial<TablePaginatedOptions> = {
    pagination: {
      changePage: setPage,
      changeRows: setRows,
      currentPage: paginationValues.currentPage,
      itemsPerPage: paginationValues.rowsPerPage,
    },
    setSortBy,
    sortBy: paginationValues.sortBy,
    onSearchChange: setSearchQuery,
    filters: [
      {
        name: 'type',
        placeholder: t('Tipo'),
        options: createTableFilterOptions({
          options: productionCenterTypeOptions,
          allOptionLabel: t('Cualquier tipo'),
        }),
        onChange: value => {
          setFilters({
            type: value as unknown as ProductionCenterType | null,
          });
        },
      },
      {
        name: 'updated_by',
        placeholder: t('Actualizado por'),
        options: createTableFilterOptions({
          options: userOptions,
          allOptionLabel: t('Actualizado por cualquiera'),
        }),
        onChange: value => {
          setFilters({
            updatedByIn: [value as unknown as string],
          });
        },
      },
    ],
    searchBarPlaceholder: t('Buscar por centro de producción'),
  };

  const columns: Column<ProductionCenterType>[] = useMemo(
    () => [
      {
        id: 'name',
        title: t('Nombre'),
        onColumnClick: item => onSelect?.(item),
        render: item => {
          return <TableRow showTooltip content={item.name} />;
        },
      },
      {
        id: 'description',
        title: t('Descripción'),
        render: item => {
          return <TableRow content={item.description || ''} />;
        },
      },
      {
        id: 'type',
        title: t('Tipo'),
        render: item => {
          const portainerGroup = portainerGroups.find(
            portainerGroup => portainerGroup.id === item.parameters?.group_id
          );
          return (
            <TableRow title={item.production_center_type} content={portainerGroup?.name ?? '-'} />
          );
        },
      },
      {
        id: 'updated_by',
        title: t('Actualizado por'),
        render: item => {
          return <TableUpdatedBy item={item} />;
        },
      },
      {
        id: 'actions',
        title: '',
        className: 'items-end',
        width: '5%',
        render: item => (
          <RenderActionButtons
            item={item}
            onUpdate={() => onUpdate?.(item)}
            onDelete={() => onDelete?.(item)}
          />
        ),
      },
    ],
    [t, onSelect, onUpdate, onDelete]
  );

  return (
    <TablePaginated
      options={options}
      columns={columns}
      data={items}
      activeItem={activeItem}
      totalItems={totalItems}
      isFetching={isFetching}
      ignoreEmpty
    />
  );
}
